<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OrderConfigurationsAPI from 'dashboard/api/orderConfigurations';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const SECTIONS = [
  { key: 'address', labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.ADDRESS' },
  { key: 'client', labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.CLIENT' },
  { key: 'meter', labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.METER' },
  { key: 'readings', labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.READINGS' },
  {
    key: 'unpaidBills',
    labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.UNPAID_BILLS',
  },
  {
    key: 'debtSummary',
    labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.DEBT_SUMMARY',
  },
  { key: 'comments', labelKey: 'ORDER_FIELD_VISIBILITY.SECTIONS.COMMENTS' },
];

const visibility = ref({});
const orderTypes = ref([]);
const activeTab = ref(null);
const loading = ref(true);
const isSaving = ref(false);

const activeTabLabel = computed(() => {
  if (!activeTab.value) return '';
  const ot = orderTypes.value.find(
    o => String(o.code) === String(activeTab.value)
  );
  return ot ? `${ot.code} - ${ot.description}` : activeTab.value;
});

const currentSections = computed(() => {
  if (!activeTab.value || !visibility.value[activeTab.value]) return {};
  return visibility.value[activeTab.value];
});

const fetchData = async () => {
  try {
    loading.value = true;
    const [visRes, typesRes] = await Promise.all([
      OrderConfigurationsAPI.get(),
      CeaLookupsAPI.get('order_types'),
    ]);
    visibility.value = visRes.data;
    orderTypes.value = typesRes.data;

    // Set active tab to first order type that has config, or first order type
    const configuredCodes = Object.keys(visibility.value);
    if (configuredCodes.length > 0) {
      activeTab.value = configuredCodes[0];
    } else if (orderTypes.value.length > 0) {
      activeTab.value = String(orderTypes.value[0].code);
    }
  } catch {
    useAlert(t('ORDER_FIELD_VISIBILITY.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const toggleSection = sectionKey => {
  if (!activeTab.value) return;
  if (!visibility.value[activeTab.value]) {
    visibility.value[activeTab.value] = {};
    SECTIONS.forEach(s => {
      visibility.value[activeTab.value][s.key] = true;
    });
  }
  visibility.value[activeTab.value][sectionKey] =
    !visibility.value[activeTab.value][sectionKey];
};

const addOrderType = code => {
  const codeStr = String(code);
  if (visibility.value[codeStr]) return;
  visibility.value[codeStr] = {};
  SECTIONS.forEach(s => {
    visibility.value[codeStr][s.key] = true;
  });
  activeTab.value = codeStr;
};

const removeOrderType = code => {
  const codeStr = String(code);
  delete visibility.value[codeStr];
  const keys = Object.keys(visibility.value);
  activeTab.value = keys.length > 0 ? keys[0] : null;
};

const save = async () => {
  try {
    isSaving.value = true;
    const response = await OrderConfigurationsAPI.update(visibility.value);
    visibility.value = response.data;
    useAlert(t('ORDER_FIELD_VISIBILITY.SAVE_SUCCESS'));
  } catch {
    useAlert(t('ORDER_FIELD_VISIBILITY.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const unconfiguredTypes = computed(() => {
  return orderTypes.value.filter(ot => !visibility.value[String(ot.code)]);
});

onMounted(fetchData);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('ORDER_FIELD_VISIBILITY.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('ORDER_FIELD_VISIBILITY.TITLE')"
        :description="$t('ORDER_FIELD_VISIBILITY.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-save"
            :label="$t('ORDER_FIELD_VISIBILITY.SAVE')"
            :is-loading="isSaving"
            @click="save"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <div class="flex items-center gap-2 flex-wrap">
        <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-1 w-fit flex-wrap">
          <button
            v-for="code in Object.keys(visibility)"
            :key="code"
            type="button"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="
              activeTab === code
                ? 'bg-n-solid-2 text-n-slate-12 font-medium shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = code"
          >
            {{ code }}
          </button>
        </div>
        <div v-if="unconfiguredTypes.length" class="relative">
          <select
            class="px-3 py-1.5 text-sm rounded-md bg-n-solid-3 border border-n-weak text-n-slate-11 cursor-pointer"
            @change="
              e => {
                if (e.target.value) addOrderType(e.target.value);
                e.target.value = '';
              }
            "
          >
            <option value="">
              + {{ $t('ORDER_FIELD_VISIBILITY.ADD_TYPE') }}
            </option>
            <option
              v-for="ot in unconfiguredTypes"
              :key="ot.id"
              :value="ot.code"
            >
              {{ ot.code }} - {{ ot.description }}
            </option>
          </select>
        </div>
      </div>
    </template>

    <template #body>
      <div
        v-if="activeTab && visibility[activeTab]"
        class="flex flex-col gap-4"
      >
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('ORDER_FIELD_VISIBILITY.TYPE_LABEL') }}: {{ activeTabLabel }}
          </h3>
          <button
            class="text-xs text-n-ruby-11 hover:text-n-ruby-12"
            @click="removeOrderType(activeTab)"
          >
            {{ $t('ORDER_FIELD_VISIBILITY.REMOVE_TYPE') }}
          </button>
        </div>

        <div class="flex flex-col gap-2">
          <div
            v-for="section in SECTIONS"
            :key="section.key"
            class="flex items-center justify-between p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <span class="font-medium text-n-slate-12">
              {{ $t(section.labelKey) }}
            </span>
            <button
              type="button"
              class="relative w-10 h-6 rounded-full transition-colors"
              :class="
                currentSections[section.key] ? 'bg-n-brand' : 'bg-n-alpha-3'
              "
              @click="toggleSection(section.key)"
            >
              <span
                class="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                :class="
                  currentSections[section.key]
                    ? 'ltr:translate-x-[18px] rtl:-translate-x-[18px]'
                    : 'ltr:translate-x-0.5 rtl:-translate-x-0.5'
                "
              />
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="!loading"
        class="flex flex-col items-center justify-center py-12 text-n-slate-10"
      >
        <span class="i-lucide-settings text-4xl mb-3" />
        <p>{{ $t('ORDER_FIELD_VISIBILITY.EMPTY') }}</p>
      </div>
    </template>
  </SettingsLayout>
</template>
